import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { Button, Space, Typography } from 'antd';

import { formatDramaTimestamp } from '@/pages/drama-review/utils';
import { getDetailLink } from '@/pages/ugc-reports/utils';

import type { ProColumns } from '@ant-design/pro-components';
import type { ReportReasonDictionary } from '@/pages/ugc-reports/utils';
import type { UgcReportTicket } from '@/services/ugc-reports';
import ReportReasonSummary from '@/pages/ugc-reports/components/ReportReasonSummary';

export type TrailingColumnOptions = {
    dictionary: ReportReasonDictionary;
    expandedRowKeys: string[];
    toggleExpand: (record: UgcReportTicket) => void;
    navigate: (path: string) => void;
    countLabelId?: string;
    actionLabelId?: string;
};

export const buildTrailingReportColumns = ({
    dictionary,
    expandedRowKeys,
    toggleExpand,
    navigate,
    countLabelId = 'ugcReports.columns.reportCount',
    actionLabelId = 'ugcReports.viewDetail',
}: TrailingColumnOptions) => ({
    reasonColumn: {
        title: <FormattedMessage id="ugcReports.columns.reasonSummary" />,
        width: 240,
        render: (_: unknown, record: UgcReportTicket) => (
            <ReportReasonSummary logs={record.logs} dictionary={dictionary} />
        ),
    } as ProColumns<UgcReportTicket>,
    timeRangeColumn: {
        title: <FormattedMessage id="ugcReports.columns.reportTimeRange" />,
        width: 170,
        render: (_: unknown, record: UgcReportTicket) => (
            <Space direction="vertical" size={2}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    <FormattedMessage id="ugcReports.columns.firstLabel" />
                    <Typography.Text strong style={{ fontSize: 12 }}>
                        {formatDramaTimestamp(record.first_reported_at)}
                    </Typography.Text>
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    <FormattedMessage id="ugcReports.columns.latestLabel" />
                    <Typography.Text strong type="warning" style={{ fontSize: 12 }}>
                        {formatDramaTimestamp(record.last_reported_at)}
                    </Typography.Text>
                </Typography.Text>
            </Space>
        ),
    } as ProColumns<UgcReportTicket>,
    reportCountColumn: {
        title: <FormattedMessage id={countLabelId} />,
        width: 140,
        render: (_: unknown, record: UgcReportTicket) => {
            const count = record.report_count ?? record.logs?.length ?? 0;
            const expanded = expandedRowKeys.includes(record.id);
            const isDanger = count >= 5;

            return (
                <Button
                    type="link"
                    size="small"
                    danger={isDanger}
                    style={{ paddingInline: 0, height: 'auto' }}
                    onClick={() => toggleExpand(record)}
                >
                    <FormattedMessage
                        id={expanded ? 'ugcReports.reportCountCollapse' : 'ugcReports.reportCount'}
                        values={{ count }}
                    />
                    {expanded ? (
                        <UpOutlined style={{ marginLeft: 4, fontSize: 10 }} />
                    ) : (
                        <DownOutlined style={{ marginLeft: 4, fontSize: 10 }} />
                    )}
                </Button>
            );
        },
    } as ProColumns<UgcReportTicket>,
    actionColumn: {
        title: <FormattedMessage id="ugcReports.columns.action" />,
        width: 180,
        fixed: 'right' as const,
        render: (_: unknown, record: UgcReportTicket) => {
            const link = getDetailLink(record);
            if (!link) return '-';
            return (
                <Button type="link" size="small" onClick={() => navigate(link)}>
                    <FormattedMessage id={actionLabelId} />
                </Button>
            );
        },
    } as ProColumns<UgcReportTicket>,
});
